import { db } from "./firebase";
import { ref, get } from "firebase/database";
import { verifyAuth } from "./authFunction";

const ROOT_PATH = "contacthq";

// Helper function from your User Dashboard
function formatDateRelative(dateString) {
  if (!dateString) return "N/A";
  const diff = Math.floor((new Date() - new Date(dateString)) / 1000);
  if (diff < -60) return "Future";
  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export const getDashboardData = async () => {
  try {
    const auth = await verifyAuth();
    if (!auth.success) {
      return { success: false, error: "Unauthorized" };
    }

    // 1. Fetch Core Nodes
    const nodes = {
      companies: `${ROOT_PATH}/companies`,
      tickets: `${ROOT_PATH}/tickets`,
      bugReports: `${ROOT_PATH}/bug-reports`,
      enquiries: `${ROOT_PATH}/contact-support`,
      admins: `${ROOT_PATH}/admins`,
    };

    const fetchPromises = Object.entries(nodes).map(async ([key, path]) => {
      const snap = await get(ref(db, path));
      return [key, snap.exists() ? snap.val() : {}];
    });

    const results = await Promise.all(fetchPromises);
    const rawData = Object.fromEntries(results);

    let liveNotifications = [];
    let allTransactions = [];

    // --- SIGNAL: NEW BUGS ---
    Object.values(rawData.bugReports || {}).forEach((bug) => {
      if (bug.status === "unresolved") {
        liveNotifications.push({
          title: `New Bug: ${bug.title}`,
          time: formatDateRelative(bug.createdAt),
          timestamp: new Date(bug.createdAt).getTime(),
          isRead: false,
          type: "bug",
        });
      }
    });

    // --- SIGNAL: TICKETS & MESSAGES ---
    Object.values(rawData.tickets || {}).forEach((ticket) => {
      if (ticket.status === "Open") {
        liveNotifications.push({
          title: `New Ticket: ${ticket.subject}`,
          time: formatDateRelative(ticket.createdAt),
          timestamp: new Date(ticket.createdAt).getTime(),
          isRead: false,
          type: "ticket",
        });
      }
      const hasUnseen = ticket.messages?.some(
        (m) => m.role === "user" && m.status === "unseen",
      );
      if (hasUnseen) {
        liveNotifications.push({
          title: `Message from ${ticket.requester?.name || "User"}`,
          time: "Just now",
          timestamp: Date.now(),
          isRead: false,
          type: "message",
        });
      }
    });

    // --- SIGNAL: TRANSACTIONS & USERS ---
    Object.values(rawData.companies || {}).forEach((company) => {
      const isNewUser =
        new Date(company.createdAt) > new Date(Date.now() - 86400000);
      if (isNewUser) {
        liveNotifications.push({
          title: `New Registration: ${company.name}`,
          time: formatDateRelative(company.createdAt),
          timestamp: new Date(company.createdAt).getTime(),
          isRead: false,
          type: "user",
        });
      }

      // ✅ Updated to match 'paymentHistory' and 'date' fields from reference image
      if (company.paymentHistory) {
        Object.values(company.paymentHistory).forEach((tx) => {
          allTransactions.push({
            ...tx,
            userName: company.name,
            userEmail: company.email || "N/A",
            // Fallback for sorting if tx.date is missing for any reason
            timestamp: tx.date || new Date().toISOString(),
          });

          if (tx.date && new Date(tx.date) > new Date(Date.now() - 3600000)) {
            liveNotifications.push({
              title: `Transaction: ₹${tx.amount} received`,
              time: formatDateRelative(tx.date),
              timestamp: new Date(tx.date).getTime(),
              isRead: false,
              type: "payment",
            });
          }
        });
      }
    });

    // --- DYNAMIC INFRASTRUCTURE AUDIT LOG ---
    const dynamicAdminActivities = [
      ...Object.values(rawData.companies || {}).map((c) => ({
        title: `Node Registered: ${c.name}`,
        timeRaw: c.createdAt,
        icon: "person_add",
      })),
      ...Object.values(rawData.tickets || {}).map((t) => ({
        title: `Ticket Logged: ${t.subject}`,
        timeRaw: t.createdAt,
        icon: "confirmation_number",
      })),
      ...Object.values(rawData.bugReports || {}).map((b) => ({
        title: `Bug Reported: ${b.title}`,
        timeRaw: b.createdAt,
        icon: "bug_report",
      })),
      // ✅ Updated to use tx.date for timeline sorting
      ...allTransactions.map((tx) => ({
        title: `Payment Processed: ₹${tx.amount}`,
        timeRaw: tx.date,
        icon: "payments",
      })),
    ]
      .filter((a) => a.timeRaw)
      .sort((a, b) => new Date(b.timeRaw) - new Date(a.timeRaw))
      .slice(0, 10)
      .map((a) => ({
        title: a.title,
        time: formatDateRelative(a.timeRaw),
        icon: a.icon,
      }));

    return {
      success: true,
      data: {
        ...rawData,
        notifications: liveNotifications.sort(
          (a, b) => b.timestamp - a.timestamp,
        ),
        // ✅ Updated sorting to rely on tx.date
        transactions: allTransactions.sort(
          (a, b) => new Date(b.date) - new Date(a.date),
        ),

        adminActivities: dynamicAdminActivities,

        userActivitySignal: [40, 70, 45, 90, 65, 80, 50, 85, 40, 60],

        stats: {
          adminsCount: Object.keys(rawData.admins || {}).length,
          usersCount: Object.keys(rawData.companies || {}).length,
          activeTicketsCount: Object.values(rawData.tickets || {}).filter(
            (t) => t.status === "Open",
          ).length,
          openBugsCount: Object.values(rawData.bugReports || {}).filter(
            (b) => b.status === "unresolved",
          ).length,
          transactionsCount: allTransactions.length,
          queriesCount: Object.keys(rawData.enquiries || {}).length,
        },
      },
    };
  } catch (error) {
    console.error("🔥 Dashboard Data Fetch Error:", error);
    return { success: false, error: error.message };
  }
};
